/**
 * AI Response Utilities
 * Cleans markdown formatting from AI-generated content
 */

/**
 * Remove markdown formatting symbols from AI-generated text.
 * Keeps the content clean for clinical documentation display.
 * 
 * @param {string} text - The text to clean
 * @returns {string} Clean text without markdown formatting
 */
export function cleanMarkdown(text) {
  if (!text) return text;
  
  let cleaned = text;
  
  // Remove markdown headers (# ## ### etc)
  cleaned = cleaned.replace(/^#{1,6}\s*/gm, '');
  
  // Remove bold/italic markers (* ** *** _ __ ___)
  cleaned = cleaned.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
  cleaned = cleaned.replace(/_{1,3}([^_]+)_{1,3}/g, '$1');
  
  // Remove inline code backticks
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
  
  // Remove code blocks
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');
  
  // Remove horizontal rules
  cleaned = cleaned.replace(/^[-*_]{3,}\s*$/gm, '');
  
  // Remove blockquotes
  cleaned = cleaned.replace(/^>\s*/gm, '');
  
  // Remove link formatting [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  
  // Remove image formatting ![alt](url)
  cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
  
  // Clean up bullet points (convert * and + to -)
  cleaned = cleaned.replace(/^\s*[*+]\s+/gm, '- ');
  
  // Remove excessive whitespace
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.replace(/ {2,}/g, ' ');
  
  return cleaned.trim();
}

/**
 * Clean AI response specifically for clinical documentation display.
 * Optionally preserves some structure like numbered lists.
 * 
 * @param {string} text - The text to clean
 * @param {boolean} preserveStructure - If true, keeps numbered lists and basic structure
 * @returns {string} Clean clinical text
 */
export function cleanClinicalResponse(text, preserveStructure = true) {
  if (!text) return text;
  
  // First apply general markdown cleaning
  let cleaned = cleanMarkdown(text);
  
  if (!preserveStructure) {
    // Remove all list formatting
    cleaned = cleaned.replace(/^\s*\d+\.\s*/gm, '');
    cleaned = cleaned.replace(/^\s*-\s*/gm, '');
  }
  
  // Remove any remaining special characters often used in markdown
  cleaned = cleaned.replace(/[\[\]{}]/g, '');
  
  return cleaned.trim();
}

export default { cleanMarkdown, cleanClinicalResponse };
